import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Form, Input, Select } from "antd";
import { unwrapResult } from "@reduxjs/toolkit";

import { Persistor } from "../../..";
import { NavContext } from "../../../App";
import { CANAL_OPTIONS, DELAI_RESOLUTION_OPTIONS } from "../../../constants";
import Snackbars from "../../../GlobalComponents/Alerts/Snackbars";
import { Colors } from "../../../GlobalComponents/Utility/Colors";
import { fetchChaines } from "../../../ReduxTLK/features/Chaines/ChaineSlice";
import {
  fetchPventes,
  fetchPventesIntervenant,
} from "../../../ReduxTLK/features/Pointdeventes/PointdeventeSlice";
import {
  resetNewTicketDraft,
  setNewTicketDraft,
} from "../../../ReduxTLK/features/SAV/NewTicketDraftSlice";
import {
  array_impact,
  array_priorite,
} from "../../../ReduxTLK/features/SAV/ticketList";
import { addNewTicket } from "../../../ReduxTLK/features/SAV/TicketSlice";
import {
  removeTabById,
  setLastActiveTab,
} from "../../../ReduxTLK/features/Tabs/TabsSlice";
import { fetchUsersAgentSAV } from "../../../ReduxTLK/features/User/UserSlice";
import { ticketService } from "../../../service/ticketService";
import { getSubCategories } from "../../../utils/savUtils";
import ConfirmResetForm from "./ConfirmResetForm";

import styles from "./New.module.css";

const { TextArea } = Input;

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const FieldLabel = ({ children, required = false }) => (
  <label className={styles.label}>
    {children}
    {required && <span className={styles.required}>*</span>}
  </label>
);

const Field = ({ label, required, children, className = "" }) => (
  <div className={`${styles.field} ${className}`}>
    <FieldLabel required={required}>{label}</FieldLabel>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export default function New() {
  const dispatch = useDispatch();
  const context = React.useContext(NavContext);
  const [form] = Form.useForm();

  /* Refs to guard against double submission */
  const isSubmittingRef = useRef(false);

  /* ---------- Local state ---------- */
  const [open, setOpen] = useState(false);
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");

  const [etablissement, setEtablissement] = useState();
  const [pointDeVente, setPointDeVente] = useState();
  const [SAVCategories, setSAVCategories] = useState([]);

  const [priorite, setPriorite] = useState();
  const [onePV, setOnePV] = useState(false);
  const [pVName, setPVName] = useState("");

  const [selectedChaine, setSelectedChaine] = useState(null);
  const [selectedPV, setSelectedPV] = useState(null);

  const [dirty, setDirty] = useState(false);
  const [allFieldsEmpty, setAllFieldsEmpty] = useState(true);
  const [openConfirmReset, setOpenConfirmReset] = useState(false);

  /* Loading state for save button (prevents double submission) */
  const [isSaving, setIsSaving] = useState(false);

  /* ---------- Redux state ---------- */
  const newTicketDraft = useSelector(
    (state) => state.draftNewTicket.newTicketDraft
  );
  const dirtyNewTicketDraft = useSelector(
    (state) => state.draftNewTicket.dirty
  );
  const agentsSAV = useSelector((state) => state.user.agentsSAV);
  const intervenant = useSelector(
    (state) => state.pointDeVente.pventesIntervenants
  );
  const chaines = useSelector((state) => state.chaine.chaines);
  const pointDeVentes = useSelector((state) => state.pointDeVente.pventes);

  const initialValues = {
    etablissement: "",
    pointDeVente: "",
    type: "",
    Sujet: "",
    priorite: "",
    Description: "",
    machine: "",
    idAssignee: "",
    impact: 0,
    intervenantId: "",
    canal: "",
    delaiResolution: "",
  };

  /* ---------- Effects ---------- */
  useEffect(() => {
    if (context.confirmedReload === true) {
      dispatch(resetNewTicketDraft());
    }
  }, [context.confirmedReload]);

  useEffect(() => {
    if (dirtyNewTicketDraft === true) {
      form.setFieldsValue(newTicketDraft);
      dispatch(setNewTicketDraft(newTicketDraft));
      const isFormEmpty = () => {
        const fields = form.getFieldsValue();
        form.setFieldsValue(fields);
        if (
          fields.etablissement !== undefined &&
          fields.etablissement !== null
        ) {
          setEtablissement(fields.etablissement);
        }
        if (fields.pointDeVente !== undefined && fields.pointDeVente !== null) {
          dispatch(fetchPventesIntervenant(fields.pointDeVente));
        }
        delete fields.impact;
        return Object.values(fields).every(
          (value) => value === "" || value === undefined || value === null
        );
      };
      setAllFieldsEmpty(isFormEmpty());
    } else {
      form.resetFields();
      dispatch(resetNewTicketDraft());
    }
  }, [dirtyNewTicketDraft]);

  useEffect(() => {
    ticketService
      .getSAVCategories()
      .then((res) => setSAVCategories(res))
      .catch((error) => {
        console.error("Error fetching SAV categories:", error);
      });
  }, []);

  useEffect(() => {
    dispatch(fetchUsersAgentSAV());
    dispatch(fetchChaines());
  }, []);

  useEffect(() => {
    if (etablissement) {
      dispatch(fetchPventes(etablissement));
    }
  }, [etablissement]);

  /* ---------- Handlers ---------- */

  const onChangePV = (item) => {
    setPointDeVente(item);
    dispatch(fetchPventesIntervenant(item));
  };

  const validateNonEmptyContent = (rule, value, callback) => {
    if (!value || value.trim() === "") {
      callback("Veuillez fournir un texte pertinent.");
    } else {
      callback();
    }
  };

  const handleClearPrioriteSelection = () => {
    form.setFieldsValue({ priorite: null });
    setPriorite("");
    checkFormEmpty();
  };

  const handleClearAssigneeSelection = () => {
    form.setFieldsValue({ idAssignee: null });
    checkFormEmpty();
  };

  const checkFormEmpty = () => {
    const fields = form.getFieldsValue();
    delete fields.impact;
    const isEmpty = Object.values(fields).every(
      (value) => value === "" || value === undefined || value === null
    );
    setAllFieldsEmpty(isEmpty);
  };

  const onValuesChange = (changedValues, allValues) => {
    const [key, value] = Object.entries(changedValues)[0];

    if (key === "priorite") {
      setPriorite(value);
    }

    if (key === "etablissement") {
      Persistor.persist();
      form.setFieldsValue({
        etablissement: value,
        pointDeVente: null,
        intervenantId: null,
      });
      dispatch(setNewTicketDraft(allValues));
      dispatch(fetchPventesIntervenant());

      setOnePV(false);
      setEtablissement(value);

      dispatch(fetchPventes(value))
        .then(unwrapResult)
        .then((res) => {
          if (res.data.data.result.length === 1) {
            setPointDeVente(res.data.data.result[0].id);
            setPVName(res.data.data.result[0].nom);
            setOnePV(true);
            dispatch(fetchPventesIntervenant(res.data.data.result[0].id));
            form.setFieldsValue({
              pointDeVente: res.data.data.result[0].id,
            });
          }
        });
    }

    if (key !== "etablissement") {
      setDirty(true);
      dispatch(setNewTicketDraft(allValues));
      Persistor.persist();
    }
  };

  /* ---------- Submission with double-click guard ---------- */
  const onFinish = (values) => {
    /* Hard guard: if a submission is already in flight, ignore */
    if (isSubmittingRef.current || isSaving) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSaving(true);

    dispatch(addNewTicket({ ...values }))
      .then(unwrapResult)
      .then((res) => {
        if ((res?.data?.status).toLowerCase() === "success") {
          setSeverity("success");
          setMessage("Le nouveau ticket a été ajouté avec succès");
          setOpen(true);
        }

        setSelectedChaine(null);
        setSelectedPV(null);
        form.resetFields();
        dispatch(resetNewTicketDraft());

        setTimeout(() => {
          const tabToDeleteIndex = context.tabs.findIndex(
            (tab) => tab.id === 2
          );
          const updatedTabs = context.tabs.filter(
            (tab, index) => index !== tabToDeleteIndex
          );

          context.setTabs(updatedTabs);
          dispatch(removeTabById(2));
          context.setActiveTab(3);
          dispatch(setLastActiveTab(3));
          Persistor.persist();

          /* Release the lock after tab navigation */
          isSubmittingRef.current = false;
          setIsSaving(false);
        }, 1000);
      })
      .catch((error) => {
        setSeverity("error");
        setMessage("Un problème est survenu lors de la création du ticket !");
        setOpenErrorBox(true);

        /* Release lock immediately on error so user can retry */
        isSubmittingRef.current = false;
        setIsSaving(false);
      });
  };

  const resetForm = () => {
    if (dirtyNewTicketDraft) {
      setOpenConfirmReset(true);
    } else {
      form.resetFields();
    }
  };

  const filterOption = (input, option) =>
    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  /* ---------- Render ---------- */
  const showActionBar = !allFieldsEmpty;
  const saveDisabled = !dirtyNewTicketDraft || isSaving;
  const cancelDisabled = !dirtyNewTicketDraft || isSaving;

  return (
    <>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <AddCircleOutlineIcon sx={{ fontSize: 22 }} />
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Ajouter un Ticket</h1>
            <p className={styles.subtitle}>
              Remplissez les informations ci-dessous pour créer un nouveau
              ticket SAV
            </p>
          </div>
        </div>

        {/* Form */}
        <Form
          form={form}
          name="control-hooks"
          onValuesChange={onValuesChange}
          initialValues={initialValues}
          onFinish={onFinish}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <div className={styles.formScroll}>
            <div className={styles.formGrid}>
              {/* Left column: 6 fields in 2x3 grid */}
              <div className={styles.leftColumn}>
                <Field label="Entreprise" required>
                  <Form.Item
                    name="etablissement"
                    rules={[
                      { required: true, message: "Ce champ est obligatoire !" },
                    ]}
                    className={styles.formItem}
                  >
                    <Select
                      showSearch
                      filterOption={filterOption}
                      placeholder="Sélectionner une entreprise"
                    >
                      {chaines &&
                        chaines.map((chaine) => (
                          <option key={chaine.id} value={chaine.id}>
                            {chaine.nom}
                          </option>
                        ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Établissement" required>
                  <Form.Item
                    name="pointDeVente"
                    rules={
                      onePV
                        ? [{ required: false }]
                        : [
                            {
                              required: true,
                              message: "Ce champ est obligatoire !",
                            },
                          ]
                    }
                    initialValue={onePV ? pointDeVente : undefined}
                    className={styles.formItem}
                  >
                    <Select
                      placeholder="Sélectionner un établissement"
                      onChange={onChangePV}
                      showSearch
                      filterOption={filterOption}
                    >
                      {pointDeVentes &&
                        pointDeVentes.map((pv) => (
                          <option key={pv.id} value={pv.id}>
                            {pv.nom}
                          </option>
                        ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Canal" required>
                  <Form.Item
                    name="canal"
                    rules={[
                      { required: true, message: "Ce champ est obligatoire !" },
                    ]}
                    className={styles.formItem}
                  >
                    <Select
                      placeholder="Sélectionner un canal"
                      showSearch
                      filterOption={filterOption}
                    >
                      {CANAL_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Estimation de la résolution" required>
                  <Form.Item
                    name="délaiRésolution"
                    rules={[
                      { required: true, message: "Ce champ est obligatoire !" },
                    ]}
                    className={styles.formItem}
                  >
                    <Select
                      placeholder="Sélectionner un délai"
                      showSearch
                      filterOption={filterOption}
                    >
                      {DELAI_RESOLUTION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Catégorie" required>
                  <Form.Item
                    name="category"
                    rules={[
                      { required: true, message: "Ce champ est obligatoire !" },
                    ]}
                    className={styles.formItem}
                  >
                    <Select
                      placeholder="Sélectionner une catégorie"
                      showSearch
                      filterOption={filterOption}
                      onChange={() => {
                        form.setFieldsValue({ subCategory: undefined });
                      }}
                    >
                      {SAVCategories &&
                        Object.keys(SAVCategories).map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Sous-Catégorie" required>
                  <Form.Item
                    name="subCategory"
                    rules={[
                      { required: true, message: "Ce champ est obligatoire !" },
                    ]}
                    className={styles.formItem}
                  >
                    <Select
                      showSearch
                      filterOption={filterOption}
                      placeholder="Sélectionner une sous-catégorie"
                    >
                      {SAVCategories &&
                        getSubCategories(
                          SAVCategories,
                          form.getFieldValue("category")
                        ).map((subCategory) => (
                          <option key={subCategory} value={subCategory}>
                            {subCategory}
                          </option>
                        ))}
                    </Select>
                  </Form.Item>
                </Field>

                {/* Subject — full width */}
                <Field label="Sujet" required className={styles.fullWidth}>
                  <Form.Item
                    name="sujet"
                    rules={[
                      {
                        maxLength: 100,
                        required: true,
                        message: "Ce champ est obligatoire !",
                        validator: validateNonEmptyContent,
                      },
                    ]}
                    className={styles.formItem}
                  >
                    <Input
                      maxLength={100}
                      count={{ show: true, max: 100 }}
                      placeholder="Décrivez brièvement le sujet du ticket"
                    />
                  </Form.Item>
                </Field>

                <Field label="Impact de ticket">
                  <Form.Item
                    name="impact"
                    rules={[{ required: false }]}
                    className={styles.formItem}
                  >
                    <Select
                      showSearch
                      filterOption={filterOption}
                      placeholder="Sélectionner un impact"
                    >
                      {array_impact &&
                        array_impact.map((impact) => (
                          <option key={impact.id} value={impact.id}>
                            {impact.nom}
                          </option>
                        ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Priorité du ticket">
                  <Form.Item
                    name="priorite"
                    rules={[{ required: false }]}
                    className={styles.formItem}
                  >
                    <Select
                      allowClear
                      showSearch
                      filterOption={filterOption}
                      onClear={handleClearPrioriteSelection}
                      placeholder="Sélectionner une priorité"
                    >
                      {array_priorite &&
                        array_priorite.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nom}
                          </option>
                        ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Assigner à">
                  <Form.Item
                    name="idAssignee"
                    rules={[{ required: false }]}
                    className={styles.formItem}
                  >
                    <Select
                      allowClear
                      showSearch
                      filterOption={filterOption}
                      onClear={handleClearAssigneeSelection}
                      placeholder="Sélectionner un agent"
                    >
                      {agentsSAV &&
                        agentsSAV.map((assigne) => (
                          <option key={assigne.id} value={assigne.id}>
                            {assigne.nom}
                          </option>
                        ))}
                    </Select>
                  </Form.Item>
                </Field>

                <Field label="Intervenant" required>
                  <Form.Item
                    name="intervenantId"
                    rules={[
                      { required: true, message: "Ce champ est obligatoire !" },
                    ]}
                    className={styles.formItem}
                  >
                    <Select
                      showSearch
                      filterOption={filterOption}
                      placeholder="Sélectionner un intervenant"
                    >
                      {intervenant.map((option, index) => (
                        <MenuItem key={index} value={option.id}>
                          {`${option.nom} ${option.prenom}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </Form.Item>
                </Field>
              </div>

              {/* Right column: Description */}
              <div className={styles.rightColumn}>
                <Field
                  label="Description"
                  required
                  className={styles.descriptionField}
                >
                  <Form.Item
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: "Ce champ est obligatoire !",
                        maxLength: 350,
                        validator: validateNonEmptyContent,
                      },
                    ]}
                    className={styles.formItem}
                  >
                    <TextArea
                      autoSize={false}
                      maxLength={350}
                      count={{ show: true, max: 350 }}
                      placeholder="Décrivez en détail le problème ou la demande..."
                    />
                  </Form.Item>
                </Field>
              </div>
            </div>
          </div>

          {/* Action bar — only shown when there's data */}
          {showActionBar && (
            <div className={styles.actionBar}>
              <Button
                className={styles.cancelBtn}
                variant="outlined"
                onClick={resetForm}
                disabled={cancelDisabled}
              >
                Annuler
              </Button>
              <Button
                className={styles.saveBtn}
                type="submit"
                variant="contained"
                disabled={saveDisabled}
              >
                {isSaving ? (
                  <>
                    <span className={styles.spinner} />
                    Enregistrement...
                  </>
                ) : (
                  "Sauvegarder le ticket"
                )}
              </Button>
            </div>
          )}
        </Form>

        <Snackbars
          severity="success"
          message={message}
          open={open}
          setOpen={setOpen}
          autohideduration={1000}
        />
        <Snackbars
          severity="error"
          message={message}
          open={openErrorBox}
          setOpen={setOpenErrorBox}
          autohideduration={1000}
        />
      </div>

      <ConfirmResetForm
        open={openConfirmReset}
        setOpen={setOpenConfirmReset}
        reset={form}
        setSelectedChaine={setSelectedChaine}
        setSelectedPV={setSelectedPV}
        setDirty={setDirty}
        dirtyNewTicketDraft={dirtyNewTicketDraft}
        setAllFieldsEmpty={setAllFieldsEmpty}
      />
    </>
  );
}